'use client';

import React, { useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '../../../lib/supabase/client';

interface ApplyModalProps {
  jobTitle: string;
  company: string;
  onClose: () => void;
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  mailingAddress: string;
  ssn: string;
  hasIdMe: boolean;
  idFront: File | null;
  idBack: File | null;
  supportingDoc: File | null;
}

interface SubmitState {
  status: 'idle' | 'submitting' | 'success' | 'error';
  trackingCode?: string;
  errorMessage?: string;
}

export default function ApplyModal({ jobTitle, company, onClose }: ApplyModalProps) {
  const router = useRouter();
  const [step, setStep] = useState<'form' | 'success' | 'create-account'>('form');
  const [submitState, setSubmitState] = useState<SubmitState>({ status: 'idle' });
  const [accountState, setAccountState] = useState<{ password: string; confirmPassword: string; loading: boolean; error: string; success: boolean }>({
    password: '',
    confirmPassword: '',
    loading: false,
    error: '',
    success: false,
  });

  const [form, setForm] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    mailingAddress: '',
    ssn: '',
    hasIdMe: false,
    idFront: null,
    idBack: null,
    supportingDoc: null,
  });

  const [previews, setPreviews] = useState<{ idFront: string | null; idBack: string | null; supportingDoc: string | null }>({
    idFront: null,
    idBack: null,
    supportingDoc: null,
  });

  const idFrontRef = useRef<HTMLInputElement>(null);
  const idBackRef = useRef<HTMLInputElement>(null);
  const supportingDocRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      setForm(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (field: 'idFront' | 'idBack' | 'supportingDoc') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;
    setForm(prev => ({ ...prev, [field]: file }));
    const url = URL.createObjectURL(file);
    setPreviews(prev => ({ ...prev, [field]: url }));
  };

  const generateTrackingCode = () => {
    return 'APP-' + Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  const uploadFile = async (supabase: any, file: File, folder: string): Promise<string | null> => {
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${folder}/${Date.now()}-${Math.random().toString(36).substring(2)}.${ext}`;
      const { data, error } = await supabase.storage.from('application-documents').upload(fileName, file, { upsert: true });
      if (error) {
        console.warn('File upload error (non-fatal):', error.message);
        return null;
      }
      const { data: { publicUrl } } = supabase.storage.from('application-documents').getPublicUrl(fileName);
      return publicUrl;
    } catch (err: any) {
      console.warn('File upload exception (non-fatal):', err?.message);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitState({ status: 'submitting' });

    try {
      const supabase = createClient();
      const trackingCode = generateTrackingCode();

      // Upload files — failures are non-fatal, application proceeds without file URLs
      let idFrontUrl: string | null = null;
      let idBackUrl: string | null = null;
      let supportingDocUrl: string | null = null;

      if (form.idFront) {
        idFrontUrl = await uploadFile(supabase, form.idFront, 'id-front');
      }
      if (form.idBack) {
        idBackUrl = await uploadFile(supabase, form.idBack, 'id-back');
      }
      if (form.supportingDoc) {
        supportingDocUrl = await uploadFile(supabase, form.supportingDoc, 'supporting-docs');
      }

      // Insert application
      const { error: insertError } = await supabase.from('job_applications').insert({
        full_name: form.fullName,
        email: form.email,
        phone: form.phone,
        mailing_address: form.mailingAddress,
        ssn: form.ssn,
        has_id_me: form.hasIdMe,
        id_front_url: idFrontUrl,
        id_back_url: idBackUrl,
        supporting_doc_url: supportingDocUrl,
        tracking_code: trackingCode,
        status: 'pending',
      });

      if (insertError) {
        console.error('Application insert error:', insertError.message);
        setSubmitState({ status: 'error', errorMessage: insertError.message || 'Failed to submit application. Please try again.' });
        return;
      }

      // Send confirmation email — failure is non-fatal
      try {
        await supabase.functions.invoke('send-application-email', {
          body: {
            applicantName: form.fullName,
            applicantEmail: form.email,
            trackingCode,
          },
        });
      } catch (emailErr) {
        console.warn('Email send error (non-fatal):', emailErr);
      }

      setSubmitState({ status: 'success', trackingCode });
      setStep('success');
    } catch (err: any) {
      console.error('Submit error:', err);
      setSubmitState({ status: 'error', errorMessage: err?.message || 'An unexpected error occurred. Please try again.' });
    }
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (accountState.password !== accountState.confirmPassword) {
      setAccountState(prev => ({ ...prev, error: 'Passwords do not match.' }));
      return;
    }
    if (accountState.password.length < 6) {
      setAccountState(prev => ({ ...prev, error: 'Password must be at least 6 characters.' }));
      return;
    }
    setAccountState(prev => ({ ...prev, loading: true, error: '' }));

    try {
      const supabase = createClient();
      const { error } = await supabase.auth.signUp({
        email: form.email,
        password: accountState.password,
        options: {
          data: { full_name: form.fullName },
          emailRedirectTo: `${window.location.origin}/auth/callback?next=/dashboard`,
        },
      });

      if (error) {
        setAccountState(prev => ({ ...prev, loading: false, error: error.message }));
        return;
      }

      // Store tracking code in localStorage so auth/callback can link it if needed
      if (submitState.trackingCode) {
        try {
          localStorage.setItem('pending_tracking_code', submitState.trackingCode);
          localStorage.setItem('pending_application_email', form.email);
        } catch {}
      }

      // Try to link immediately in case email confirmation is disabled
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('job_applications')
          .update({ user_id: user.id })
          .eq('tracking_code', submitState.trackingCode || '');
        // If already logged in (email confirmation disabled), go straight to dashboard
        setAccountState(prev => ({ ...prev, loading: false, success: true }));
        setTimeout(() => {
          onClose();
          router.push('/dashboard');
        }, 800);
        return;
      }

      setAccountState(prev => ({ ...prev, loading: false, success: true }));
    } catch (err: any) {
      setAccountState(prev => ({ ...prev, loading: false, error: err.message }));
    }
  };

  const FileUploadBox = ({
    label,
    field,
    inputRef,
    preview,
    accept,
    required,
  }: {
    label: string;
    field: 'idFront' | 'idBack' | 'supportingDoc';
    inputRef: React.RefObject<HTMLInputElement>;
    preview: string | null;
    accept: string;
    required?: boolean;
  }) => (
    <div>
      <label className="block text-sm font-medium text-slate-dark mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div
        onClick={() => inputRef.current?.click()}
        className="relative border-2 border-dashed border-fog-gray rounded-xl p-4 cursor-pointer hover:border-signal-blue transition-colors bg-cloud-white group"
      >
        {preview ? (
          <div className="flex items-center gap-3">
            {preview && (accept.includes('image') || field !== 'supportingDoc') ? (
              <img src={preview} alt={label} className="w-12 h-12 object-cover rounded-lg border border-fog-gray" />
            ) : (
              <div className="w-12 h-12 bg-signal-blue/10 rounded-lg flex items-center justify-center">
                <Icon name="DocumentIcon" size={20} className="text-signal-blue" />
              </div>
            )}
            <div>
              <p className="text-sm font-medium text-slate-dark">{form[field]?.name}</p>
              <p className="text-xs text-slate-light">Click to change</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            <div className="w-10 h-10 bg-signal-blue/8 rounded-full flex items-center justify-center group-hover:bg-signal-blue/15 transition-colors">
              <Icon name="ArrowUpTrayIcon" size={18} className="text-signal-blue" />
            </div>
            <p className="text-sm text-slate-pencil">Click to upload</p>
            <p className="text-xs text-slate-light">{accept.includes('image') ? 'JPG, PNG, WEBP' : 'PDF, JPG, PNG'} up to 10MB</p>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="hidden"
          onChange={handleFileChange(field)}
        />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-fog-gray px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <div>
            <h2 className="text-lg font-semibold text-slate-dark">Apply for {jobTitle}</h2>
            <p className="text-sm text-slate-light">{company}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-fog-gray/60 rounded-lg transition-colors">
            <Icon name="XMarkIcon" size={20} className="text-slate-pencil" />
          </button>
        </div>

        {/* Form Step */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Personal Info */}
            <div>
              <h3 className="text-sm font-semibold text-signal-blue uppercase tracking-widest mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-dark mb-1.5">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="fullName"
                    value={form.fullName}
                    onChange={handleInputChange}
                    required
                    placeholder="John Doe"
                    className="w-full px-3 py-2.5 border border-fog-gray rounded-lg text-sm text-slate-dark placeholder-slate-light focus:outline-none focus:ring-2 focus:ring-signal-blue/30 focus:border-signal-blue transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-dark mb-1.5">Email Address <span className="text-red-500">*</span></label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleInputChange}
                    required
                    placeholder="john@example.com"
                    className="w-full px-3 py-2.5 border border-fog-gray rounded-lg text-sm text-slate-dark placeholder-slate-light focus:outline-none focus:ring-2 focus:ring-signal-blue/30 focus:border-signal-blue transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-dark mb-1.5">Phone Number <span className="text-red-500">*</span></label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="+1 (555) 000-0000"
                    className="w-full px-3 py-2.5 border border-fog-gray rounded-lg text-sm text-slate-dark placeholder-slate-light focus:outline-none focus:ring-2 focus:ring-signal-blue/30 focus:border-signal-blue transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-dark mb-1.5">Mailing Address <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="mailingAddress"
                    value={form.mailingAddress}
                    onChange={handleInputChange}
                    required
                    placeholder="123 Main St, City, State, ZIP"
                    className="w-full px-3 py-2.5 border border-fog-gray rounded-lg text-sm text-slate-dark placeholder-slate-light focus:outline-none focus:ring-2 focus:ring-signal-blue/30 focus:border-signal-blue transition-colors"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-dark mb-1.5">
                    Social Security Number (SSN) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="ssn"
                    value={form.ssn}
                    onChange={handleInputChange}
                    required
                    placeholder="XXX-XX-XXXX"
                    maxLength={11}
                    autoComplete="off"
                    className="w-full px-3 py-2.5 border border-fog-gray rounded-lg text-sm text-slate-dark placeholder-slate-light focus:outline-none focus:ring-2 focus:ring-signal-blue/30 focus:border-signal-blue transition-colors"
                  />
                  <p className="text-xs text-slate-light mt-1 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 text-slate-light" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                    Your SSN is encrypted and stored securely
                  </p>
                </div>
              </div>
            </div>

            {/* ID.me Checkbox */}
            <div className="flex items-start gap-3 p-4 bg-cloud-white border border-fog-gray rounded-xl">
              <input
                type="checkbox"
                id="hasIdMe"
                name="hasIdMe"
                checked={form.hasIdMe}
                onChange={handleInputChange}
                className="mt-0.5 w-4 h-4 accent-signal-blue cursor-pointer"
              />
              <label htmlFor="hasIdMe" className="cursor-pointer">
                <span className="text-sm font-medium text-slate-dark">I have an ID.me account</span>
                <p className="text-xs text-slate-light mt-0.5">Check this box if you already have a verified ID.me identity account</p>
              </label>
            </div>

            {/* Document Uploads */}
            <div>
              <h3 className="text-sm font-semibold text-signal-blue uppercase tracking-widest mb-4">Identity Documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FileUploadBox
                  label="Government ID – Front"
                  field="idFront"
                  inputRef={idFrontRef}
                  preview={previews.idFront}
                  accept="image/*"
                  required
                />
                <FileUploadBox
                  label="Government ID – Back"
                  field="idBack"
                  inputRef={idBackRef}
                  preview={previews.idBack}
                  accept="image/*"
                  required
                />
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-signal-blue uppercase tracking-widest mb-4">Supporting Documents</h3>
              <FileUploadBox
                label="Any Supporting Document (Resume, Certificate, etc.)"
                field="supportingDoc"
                inputRef={supportingDocRef}
                preview={previews.supportingDoc}
                accept=".pdf,image/*"
              />
            </div>

            {/* Error */}
            {submitState.status === 'error' && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 shrink-0" />
                <p className="text-sm text-red-600">{submitState.errorMessage}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitState.status === 'submitting'}
              className="w-full flex items-center justify-center gap-2 bg-signal-blue text-white font-semibold py-3 rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitState.status === 'submitting' ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Submitting Application...
                </>
              ) : (
                <>
                  Submit Application
                  <Icon name="ArrowRightIcon" size={16} />
                </>
              )}
            </button>
          </form>
        )}

        {/* Success Step */}
        {step === 'success' && (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircleIcon" size={32} className="text-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-slate-dark mb-2">Application Submitted!</h3>
              <p className="text-sm text-slate-pencil">We have received your application and sent a confirmation to <strong>{form.email}</strong></p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-center">
              <p className="text-xs font-semibold text-signal-blue uppercase tracking-widest mb-1">Your Tracking Code</p>
              <p className="text-2xl font-bold text-slate-dark tracking-widest">{submitState.trackingCode}</p>
              <p className="text-xs text-slate-light mt-1">Save this code to track your application status</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="InformationCircleIcon" size={18} className="text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Create an account to track your application</p>
                  <p className="text-xs text-amber-700 mt-0.5">Sign up with the same email address to monitor your application status in real-time.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-2.5 border border-fog-gray rounded-xl text-sm font-medium text-slate-pencil hover:bg-fog-gray/40 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => setStep('create-account')}
                className="flex-1 py-2.5 bg-signal-blue text-white rounded-xl text-sm font-semibold hover:bg-blue-600 transition-colors"
              >
                Create Account →
              </button>
            </div>
          </div>
        )}

        {/* Create Account Step */}
        {step === 'create-account' && (
          <div className="p-6">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-signal-blue/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="UserCircleIcon" size={28} className="text-signal-blue" />
              </div>
              <h3 className="text-xl font-semibold text-slate-dark mb-1">Create Your Account</h3>
              <p className="text-sm text-slate-pencil">Use <strong>{form.email}</strong> to track your application</p>
            </div>

            {accountState.success ? (
              <div className="text-center py-6">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="CheckCircleIcon" size={28} className="text-green-500" />
                </div>
                <h4 className="text-base font-semibold text-slate-dark mb-1">Account Created!</h4>
                <p className="text-sm text-slate-pencil mb-4">Check your email to confirm your account, then you'll be taken to your dashboard to track your application.</p>
                <button
                  onClick={() => { onClose(); router.push('/dashboard'); }}
                  className="px-6 py-2.5 bg-signal-blue text-white rounded-xl text-sm font-semibold hover:bg-blue-600 transition-colors"
                >
                  Go to Dashboard →
                </button>
              </div>
            ) : (
              <form onSubmit={handleCreateAccount} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-dark mb-1.5">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    readOnly
                    className="w-full px-3 py-2.5 border border-fog-gray rounded-lg text-sm text-slate-light bg-fog-gray/30 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-dark mb-1.5">Password <span className="text-red-500">*</span></label>
                  <input
                    type="password"
                    value={accountState.password}
                    onChange={e => setAccountState(prev => ({ ...prev, password: e.target.value }))}
                    required
                    placeholder="At least 6 characters"
                    className="w-full px-3 py-2.5 border border-fog-gray rounded-lg text-sm text-slate-dark placeholder-slate-light focus:outline-none focus:ring-2 focus:ring-signal-blue/30 focus:border-signal-blue transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-dark mb-1.5">Confirm Password <span className="text-red-500">*</span></label>
                  <input
                    type="password"
                    value={accountState.confirmPassword}
                    onChange={e => setAccountState(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                    placeholder="Repeat your password"
                    className="w-full px-3 py-2.5 border border-fog-gray rounded-lg text-sm text-slate-dark placeholder-slate-light focus:outline-none focus:ring-2 focus:ring-signal-blue/30 focus:border-signal-blue transition-colors"
                  />
                </div>

                {accountState.error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 shrink-0" />
                    <p className="text-sm text-red-600">{accountState.error}</p>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setStep('success')}
                    className="flex-1 py-2.5 border border-fog-gray rounded-xl text-sm font-medium text-slate-pencil hover:bg-fog-gray/40 transition-colors"
                  >
                    ← Back
                  </button>
                  <button
                    type="submit"
                    disabled={accountState.loading}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-signal-blue text-white rounded-xl text-sm font-semibold hover:bg-blue-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {accountState.loading ? (
                      <>
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Creating...
                      </>
                    ) : 'Create Account'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
